// BugBountyPanel — §LG2 Bug Bounty user surface. Signed-in creators report a real,
// reproducible bug and can see each report's status; an admin-ACCEPTED report earns
// exactly 100 credits (server-granted, exactly-once — see handlers/bounty.ts).
// HONESTY: submitting shows "submitted", never a promised award; the award appears
// only when the server says status === 'awarded'.
import { useCallback, useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { Bug, Loader2, Check } from 'lucide-preact';
import { useToast } from '@vibeshell/hooks';
import { t } from '../lib/i18n';

interface Report {
  id: string;
  category: string;
  status: 'submitted' | 'needs_info' | 'accepted' | 'duplicate' | 'rejected' | 'awarded';
  createdAt: number;
  awarded: number;
}

const CATEGORIES = ['generation', 'editing', 'ui', 'payment', 'data-loss', 'security', 'other'] as const;

const STATUS_COLOR: Record<Report['status'], string> = {
  submitted: 'var(--fg-muted)',
  needs_info: 'var(--color-warning, #F59E0B)',
  accepted: 'var(--color-primary)',
  duplicate: 'var(--fg-subtle)',
  rejected: 'var(--fg-subtle)',
  awarded: 'var(--color-success, #10B981)'
};

export function BugBountyPanel(): JSX.Element {
  const toast = useToast();
  const [reports, setReports] = useState<Report[] | null>(null);
  const [bounty, setBounty] = useState(100);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [category, setCategory] = useState<string>('generation');
  const [description, setDescription] = useState('');
  const [steps, setSteps] = useState('');
  const [url, setUrl] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await fetch('/v1/bounty/mine', { credentials: 'include' });
      if (!r.ok) { setReports([]); return; }
      const j = (await r.json()) as { reports?: Report[]; bounty?: number };
      setReports(j.reports ?? []);
      if (j.bounty) setBounty(j.bounty);
    } catch {
      setReports([]);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  async function submit(): Promise<void> {
    if (description.trim().length < 20 || steps.trim().length < 10) {
      toast.show(t('bb.tooShort'), { variant: 'error' });
      return;
    }
    setBusy(true);
    try {
      const r = await fetch('/v1/bounty/report', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ category, description: description.trim(), steps: steps.trim(), url: url.trim() || undefined })
      });
      const j = (await r.json().catch(() => ({}))) as { ok?: boolean; error?: string };
      if (r.ok && j.ok) {
        toast.show(t('bb.submitted'));
        setDescription(''); setSteps(''); setUrl(''); setOpen(false);
        await load();
      } else {
        toast.show(j.error === 'too_many_reports' ? t('bb.tooMany') : t('bb.submitErr'), { variant: 'error' });
      }
    } catch {
      toast.show(t('bb.submitErr'), { variant: 'error' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-testid="bug-bounty-panel" className="surface flex flex-col gap-3.5 p-5 animate-vs-fade-in" aria-label={t('bb.title')}>
      <header className="flex items-center gap-2.5">
        <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[color:var(--color-primary)]/12 text-[color:var(--color-primary)]">
          <Bug size={14} strokeWidth={2.2} />
        </span>
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold tracking-tight text-[color:var(--fg)]">{t('bb.title')}</h2>
          <p className="text-[12px] leading-tight text-[color:var(--fg-muted)]">{t('bb.sub', { n: bounty })}</p>
        </div>
      </header>

      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex h-10 items-center justify-center gap-1.5 rounded-[var(--radius-md)] border border-[color:var(--border)] px-3 text-[13px] font-semibold text-[color:var(--fg)] transition hover:bg-[color:var(--bg-elevated)]"
        >
          <Bug size={13} /> {t('bb.cta')}
        </button>
      ) : (
        <div className="flex flex-col gap-2.5 rounded-[var(--radius-lg)] border border-[color:var(--border)] bg-[color:var(--bg-subtle)] p-3.5">
          <label className="text-[12px] font-medium text-[color:var(--fg-muted)]">{t('bb.category')}</label>
          <select
            value={category}
            onChange={(e) => setCategory((e.target as HTMLSelectElement).value)}
            className="min-h-[40px] rounded-[var(--radius-md)] border border-[color:var(--border)] bg-[color:var(--bg-elevated)] px-2.5 text-[13.5px] text-[color:var(--fg)]"
          >
            {CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>{t(`bb.cat.${cat}`)}</option>
            ))}
          </select>
          <label className="text-[12px] font-medium text-[color:var(--fg-muted)]">{t('bb.desc')}</label>
          <textarea
            value={description}
            onInput={(e) => setDescription((e.target as HTMLTextAreaElement).value)}
            placeholder={t('bb.descPh')}
            rows={3}
            className="rounded-[var(--radius-md)] border border-[color:var(--border)] bg-[color:var(--bg-elevated)] px-2.5 py-2 text-[13.5px] text-[color:var(--fg)]"
          />
          <label className="text-[12px] font-medium text-[color:var(--fg-muted)]">{t('bb.steps')}</label>
          <textarea
            value={steps}
            onInput={(e) => setSteps((e.target as HTMLTextAreaElement).value)}
            placeholder={t('bb.stepsPh')}
            rows={3}
            className="rounded-[var(--radius-md)] border border-[color:var(--border)] bg-[color:var(--bg-elevated)] px-2.5 py-2 text-[13.5px] text-[color:var(--fg)]"
          />
          <input
            type="url"
            value={url}
            onInput={(e) => setUrl((e.target as HTMLInputElement).value)}
            placeholder={t('bb.urlPh')}
            className="min-h-[40px] rounded-[var(--radius-md)] border border-[color:var(--border)] bg-[color:var(--bg-elevated)] px-2.5 text-[13.5px] text-[color:var(--fg)]"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={submit}
              disabled={busy}
              className="inline-flex h-10 flex-1 items-center justify-center gap-1.5 rounded-[var(--radius-md)] bg-[color:var(--color-primary)] px-3 text-[13px] font-semibold text-white shadow-sm transition hover:bg-[color:var(--color-primary-hover)] disabled:opacity-50"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} {t('bb.submit')}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="inline-flex h-10 items-center rounded-[var(--radius-md)] px-3 text-[13px] font-medium text-[color:var(--fg-subtle)] transition hover:text-[color:var(--fg)]"
            >
              {t('bb.cancel')}
            </button>
          </div>
          <p className="text-[11px] text-[color:var(--fg-subtle)]">{t('bb.fine', { n: bounty })}</p>
        </div>
      )}

      {reports && reports.length > 0 && (
        <ul className="flex flex-col gap-1.5" aria-label={t('bb.mine')}>
          {reports.slice(0, 6).map((r) => (
            <li key={r.id} className="flex items-center justify-between rounded-[var(--radius-md)] border border-[color:var(--border)] px-3 py-2">
              <span className="text-[12.5px] text-[color:var(--fg)]">{t(`bb.cat.${r.category}`) || r.category}</span>
              <span className="text-[12px] font-semibold" style={{ color: STATUS_COLOR[r.status] ?? 'var(--fg-muted)' }}>
                {t(`bb.st.${r.status}`) || r.status}
                {r.status === 'awarded' ? ` · +${r.awarded}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
